import {useSignal, useSignals} from '@preact/signals-react/runtime'
import {useQueryClient} from '@tanstack/react-query'
import {useCallback, useEffect, useMemo, useRef} from 'react'
import {useHistory} from 'react-router-dom/cjs/react-router-dom'
import {SystemRole} from '../../../Constants'
import {useAppConfig} from '../../../context/app/AppContext'
import {useUserHospital} from '../../../context/hospital/UserHospitalContext'
import {QueryKeyManager} from '../../../query-manager/QueryKeysManager'
import {QueryKeyTypes} from '../../../query-manager/QueryKeyTypes'
import {useQueryParams} from '../../../utils/NavigatorUtils'
import {useAuth} from '../../authentication/AuthProvider'
import {getClientId} from '../../authentication/AuthUtils'
import PageContentWrapper from '../../layout/PageContentWrapper'
import {severityNotification} from '../../notification/NotificationConstant'
import {showNotification} from '../../notification/NotificationService'
import {isArrayAndNotEmpty} from '../../observations/Utils'
import {
    getCustomPatientList,
    getDetailPatient,
} from '../../patient/PatientEndPoint'
import {
    setStoragePatientPage,
    useCountCustomPatientList,
    useCustomPatientList,
} from '../../patient/query-keys/PatientKeysManager'
import {permissionOptionKeys} from '../../role/RoleConstant'
import {useRoleContext} from '../../role/RoleProvider'
import {useImportNotificationContext} from '../../table-utils/importForm/ImportNotificationProvider'
import {
    filterDataTableEvent,
    filterEventTypes,
    getFilterEventKey,
    removeFilterChangeListener,
} from '../broadcast/FilterEvent'
import {
    exportColumnTypes,
    exportRecordTypes,
    exportTypes,
    tableToolbarType,
} from '../Constant'
import {DatatableContext} from '../DataTableContext'
import StyledDataTableWrapper from '../StyledDataTableWrapper'
import {
    getAdvancedFilter,
    getShouldRememberAdvancedFilter,
    tableTypes,
} from '../StyledTableUtils'
import {
    getPatientEventKey,
    onPatientPathwayChangeRemoveListener,
    onPatientStaticListChangeRemoveListener,
    onPatientTableChangeRemoveListener,
    patientEvent,
    patientEventTypes,
} from './broastcast/PatientEvent'
import StyledPatientForm from './PatientForm'
import {columnList} from './PatientTableConstants'

const StyledPatientDatatable = () => {
    useSignals()

    const queryClient = useQueryClient()

    //#region Context
    const {user} = useAuth()
    const {userRole, timezoneId} = useAppConfig()
    const {currentHospital} = useUserHospital()
    const {patientManagementPermissions, isQualifiedForTakingCareOfPatients} =
        useRoleContext()
    const {handleOnExportDatatable} = useImportNotificationContext()
    const history = useHistory()
    const query = useQueryParams()
    //#endregion

    //#region Load Data from Local Storage
    const initialAdvancedFilterRequest = useMemo(() => {
        const didRememberSettings = getShouldRememberAdvancedFilter(
            tableTypes.patient,
        )

        if (didRememberSettings) {
            const storedFilterRequest = getAdvancedFilter(tableTypes.patient)
            if (
                !!storedFilterRequest &&
                Object.keys(storedFilterRequest).length > 0
            ) {
                return storedFilterRequest
            }
        }

        return {}
    }, [])
    //#endregion

    //#region URL Query Params
    const queryParamsAdvancedFilter = useMemo(() => {
        const selectedPathwayIds = query?.get('pathwayId')
            ? [parseInt(query?.get('pathwayId'))]
            : []

        const selectedStaticListIds = query?.get('staticListId')
            ? [parseInt(query?.get('staticListId'))]
            : []

        return {
            pathwayIds: selectedPathwayIds,
            staticListIds: selectedStaticListIds,
        }
    }, [query])
    //#endregion

    //#region State Controls
    const pageable = useSignal({
        pageRows: 10,
        pageNumber: 0,
        sortField: 'sicknessLevel',
        sortOrder: -1,
    })

    const filterRequest = useSignal({
        globalSearchValue: null,
        hospitalId:
            userRole === SystemRole.USER_WITH_ROLE ? currentHospital?.id : null,
        systemRole: SystemRole.USER,
        hcpIds: [],
        sicknessLevels: [],
        pathwayIds: [],
        staticListIds: [],
        ...initialAdvancedFilterRequest,
        ...queryParamsAdvancedFilter,
        tableViewType: isQualifiedForTakingCareOfPatients
            ? 'all'
            : 'only_assigned',
    })

    // Pageable
    const handleChangeSortColumn = useCallback(
        e => {
            pageable.value = {
                ...pageable.value,
                sortField: e.sortField,
                sortOrder: e.sortOrder,
            }
        },
        [pageable.value.sortField, pageable.value.sortOrder],
    )

    const handleChangePageNumber = useCallback(
        newPageNumber => {
            pageable.value = {
                ...pageable.value,
                pageNumber: newPageNumber,
            }

            handlePrefetchDataByPage(newPageNumber + 1)
        },
        [pageable.value.pageNumber],
    )

    const handleChangePageRows = useCallback(
        newPageRows => {
            pageable.value = {
                ...pageable.value,
                pageRows: newPageRows,
                pageNumber: 0,
            }

            pagePrefetchRef.current = [0]

            handlePrefetchDataByPage(1)
        },
        [pageable.value.pageRows],
    )

    // Global Search Value
    const handleChangeGlobalSearch = useCallback(value => {
        pageable.value = {
            ...pageable.value,
            pageNumber: 0,
        }

        filterRequest.value = {
            ...filterRequest.value,
            globalSearchValue: value || null,
        }
    }, [])

    // Filter Request
    const handleClearFilterRequest = useCallback(() => {
        filterRequest.value = {
            globalSearchValue: null,
            hospitalId:
                userRole === SystemRole.USER_WITH_ROLE
                    ? currentHospital?.id
                    : null,
            systemRole: SystemRole.USER,
            sicknessLevels: [],
            pathwayIds: [],
            staticListIds: [],
            hcpIds: [],
            tableViewType: isQualifiedForTakingCareOfPatients
                ? 'all'
                : 'only_assigned',
        }
    }, [])

    const handleClearFilterRequestByKey = useCallback((key, value) => {
        filterRequest.value = {
            ...filterRequest.value,
            [key]: value,
        }
    }, [])

    const handleClearAdvancedFilterRequest = useCallback(() => {
        filterRequest.value = {
            globalSearchValue: filterRequest.value?.globalSearchValue || null,
            hospitalId:
                userRole === SystemRole.USER_WITH_ROLE
                    ? currentHospital?.id
                    : null,
            systemRole: SystemRole.USER,
            sicknessLevels: filterRequest.value?.sicknessLevels || [],
            pathwayIds: filterRequest.value?.pathwayIds || [],
            staticListIds: filterRequest.value?.staticListIds || [],
            hcpIds: filterRequest.value?.hcpIds || [],
            tableViewType: isQualifiedForTakingCareOfPatients
                ? 'all'
                : 'only_assigned',
        }
    }, [])

    // Add New
    const formRef = useRef(null)
    const handleAddNew = useCallback(() => {
        formRef.current?.showForm()
    }, [])
    //#endregion

    //#region Check Permission
    const handleCheckPermissionByKey = useCallback(
        key => {
            if (!key) {
                return false
            }

            return (
                userRole === SystemRole.ADMIN ||
                patientManagementPermissions?.includes(key)
            )
        },
        [patientManagementPermissions, userRole],
    )
    //#endregion

    //#region Data Controls
    const getCurrentQueryKey = ({newPageNumber, newPageRows}) => {
        const {patientKeys} = QueryKeyManager(QueryKeyTypes.patient, user?.id)

        return patientKeys.customList({
            hospitalId:
                userRole === SystemRole.USER_WITH_ROLE
                    ? currentHospital?.id
                    : null,
            timezoneId: timezoneId,
            pageNumber:
                newPageNumber !== null && typeof newPageNumber === 'number'
                    ? newPageNumber
                    : pageable.value.pageNumber,
            pageRows:
                newPageRows !== null && typeof newPageNumber === 'number'
                    ? newPageRows
                    : pageable.value.pageRows,
            sortField: pageable.value.sortField,
            sortOrder: pageable.value.sortOrder,
            filterRequest: {
                ...filterRequest.value,
            },
        })
    }

    const {data: dataList} = useCustomPatientList(
        userRole === SystemRole.USER_WITH_ROLE ? currentHospital?.id : null,
        timezoneId,
        user?.id,
        pageable.value.pageNumber,
        pageable.value.pageRows,
        pageable.value.sortField,
        pageable.value.sortOrder,
        filterRequest.value,
        true,
    )

    const {data: totalRecords, isFetched} = useCountCustomPatientList(
        userRole === SystemRole.USER_WITH_ROLE ? currentHospital?.id : null,
        timezoneId,
        user?.id,
        filterRequest.value,
        true,
    )

    const queryData = useMemo(() => {
        return {
            content: dataList || [],
            numberOfElements: isArrayAndNotEmpty(dataList)
                ? dataList?.length
                : 0,
            number: pageable.value.pageNumber,

            totalPages: Math.ceil(totalRecords / pageable.value.pageRows),
            totalElements: totalRecords,
            size: pageable.value.pageRows,
            first: pageable.value.pageNumber === 0,
            last:
                pageable.value.pageNumber ===
                Math.ceil(totalRecords / pageable.value.pageRows) - 1,
        }
    }, [dataList, totalRecords])

    // Page Rows from Pagination
    const rowsOptionHoveredRef = useRef([10])
    const handlePrefetchDataByRowsOption = useCallback(rows => {
        if (!!user?.id && !rowsOptionHoveredRef.current?.includes(rows)) {
            rowsOptionHoveredRef.current.push(rows)

            queryClient.prefetchQuery({
                queryKey: getCurrentQueryKey({
                    newPageNumber: 0,
                    newPageRows: rows,
                }),
                queryFn: () =>
                    getCustomPatientList(
                        user?.id,
                        0,
                        rows,
                        pageable.peek().sortField,
                        pageable.peek().sortOrder,
                        filterRequest.peek(),
                    ),
            })
        }
    }, [])

    // Page Number from Pagination
    const pagePrefetchRef = useRef([0])
    const handlePrefetchDataByPage = useCallback(pageNumber => {
        if (!!user?.id && !pagePrefetchRef.current?.includes(pageNumber)) {
            pagePrefetchRef.current.push(pageNumber)

            queryClient.prefetchQuery({
                queryKey: getCurrentQueryKey({
                    newPageNumber: pageNumber,
                    newPageRows: pageable.peek().pageRows,
                }),
                queryFn: () =>
                    getCustomPatientList(
                        user?.id,
                        pageNumber,
                        pageable.peek().pageRows,
                        pageable.peek().sortField,
                        pageable.peek().sortOrder,
                        filterRequest.peek(),
                    ),
            })
        }
    }, [])

    // Detail Row Hovered
    const detailRowHoveredRef = useRef([])
    const handlePrefetchDataByDetailRow = useCallback(id => {
        if (!!user?.id && !detailRowHoveredRef.current?.includes(id)) {
            detailRowHoveredRef.current.push(id)

            const {patientKeys} = QueryKeyManager(
                QueryKeyTypes.patient,
                user?.id,
            )

            queryClient.prefetchQuery({
                queryKey: patientKeys.detail(id),
                queryFn: () => getDetailPatient(user?.id, id),
            })
        }
    }, [])

    const handleOnDetailRowClick = useCallback(id => {
        if (
            handleCheckPermissionByKey(
                permissionOptionKeys.allowToViewPatientInfo,
            )
        ) {
            history.push('/patients/' + id)
        } else {
            showNotification(
                severityNotification.info,
                "You don't have permission to view information !!",
            )
        }
    }, [])

    useEffect(() => {
        if (
            pageable.value.pageNumber === 0 &&
            pageable.value.pageRows === 10 &&
            isArrayAndNotEmpty(queryData?.content)
        ) {
            setStoragePatientPage(currentHospital?.id, timezoneId, queryData)
        }
    }, [queryData])
    //#endregion

    //#region Event Listener on State Changes
    useEffect(() => {
        // TABLE CHANGE
        patientEvent.on(
            `${getPatientEventKey(patientEventTypes.TABLE_CHANGE)}`,
            data => {
                if (!data || !data?.id || !data?.clientId) {
                    return
                }

                if (data?.clientId !== getClientId()) {
                    return
                }

                if (
                    userRole === SystemRole.USER_WITH_ROLE &&
                    (!data?.hospitalId ||
                        data?.hospitalId !== currentHospital?.id)
                ) {
                    return
                }

                // If the Patient table contains a user ID that has been changed.
                if (
                    !queryData?.content ||
                    !queryData?.content?.some(
                        patient => patient?.id === data?.id,
                    )
                ) {
                    return
                }

                const {patientKeys} = QueryKeyManager(
                    QueryKeyTypes.patient,
                    user?.id,
                )

                queryClient.invalidateQueries({
                    queryKey: patientKeys.pattern(),
                })
            },
        )

        // PATIENT PATHWAY CHANGE
        patientEvent.on(
            `${getPatientEventKey(patientEventTypes.PATHWAY_CHANGE)}`,
            data => {
                if (
                    !data ||
                    !data?.userIds ||
                    !data?.userIds?.length ||
                    !data?.clientId ||
                    data?.clientId !== getClientId()
                ) {
                    return
                }

                // If Current Data have any patient whose pathways have been changed
                if (
                    queryData &&
                    queryData?.content?.length &&
                    queryData?.content?.some(item =>
                        data?.userIds.includes(item?.id),
                    )
                ) {
                    const {patientKeys} = QueryKeyManager(
                        QueryKeyTypes.patient,
                        user?.id,
                    )

                    queryClient.invalidateQueries({
                        queryKey: patientKeys.pattern(),
                    })
                }
            },
        )

        // PATIENT STATIC LIST CHANGE
        patientEvent.on(
            `${getPatientEventKey(patientEventTypes.STATIC_LIST_CHANGE)}`,
            data => {
                // Client
                if (
                    !data ||
                    !data?.clientId ||
                    data?.clientId !== getClientId()
                ) {
                    return
                }

                // Hospital (ADMIN's Static List doesn't have hospital ID)
                if (
                    !!data?.hospitalId &&
                    data?.hospitalId !== currentHospital?.id &&
                    userRole === SystemRole.USER_WITH_ROLE
                ) {
                    return
                }

                // Owner
                if (user?.id === data?.ownerId) {
                    return
                }

                // User
                if (!data?.userIds || !data?.userIds?.length) {
                    return
                }

                // If Current Data have any patient whose static list have been changed
                if (
                    queryData &&
                    queryData?.content?.length &&
                    queryData?.content?.some(item =>
                        data?.userIds.includes(item?.id),
                    )
                ) {
                    const {patientKeys} = QueryKeyManager(
                        QueryKeyTypes.patient,
                        user?.id,
                    )

                    queryClient.invalidateQueries({
                        queryKey: patientKeys.pattern(),
                    })
                }
            },
        )

        handlePrefetchDataByPage(pageable.value.pageNumber + 1)

        filterDataTableEvent.on(
            `${getFilterEventKey(
                tableTypes.patient,
                filterEventTypes.advanced,
            )}`,
            newFilterConfig => {
                if (!newFilterConfig || typeof newFilterConfig !== 'object') {
                    return
                }

                if (Object.keys(newFilterConfig).length === 0) {
                    handleClearAdvancedFilterRequest()

                    return
                }

                filterRequest.value = {
                    ...filterRequest.value,
                    ...newFilterConfig,
                }
            },
        )

        filterDataTableEvent.on(
            `${getFilterEventKey(
                tableTypes.patient,
                filterEventTypes.pathway,
            )}`,
            newFilterConfig => {
                if (!newFilterConfig || typeof newFilterConfig !== 'object') {
                    return
                }

                if (Object.keys(newFilterConfig).length === 0) {
                    handleClearFilterRequestByKey('pathwayIds', [])

                    return
                }

                filterRequest.value = {
                    ...filterRequest.value,
                    ...newFilterConfig,
                }
            },
        )

        filterDataTableEvent.on(
            `${getFilterEventKey(tableTypes.patient, filterEventTypes.hcp)}`,
            newFilterConfig => {
                if (!newFilterConfig || typeof newFilterConfig !== 'object') {
                    return
                }

                if (Object.keys(newFilterConfig).length === 0) {
                    handleClearFilterRequestByKey('hcpIds', [])
                    return
                }

                filterRequest.value = {
                    ...filterRequest.value,
                    ...newFilterConfig,
                }
            },
        )

        filterDataTableEvent.on(
            `${getFilterEventKey(
                tableTypes.patient,
                filterEventTypes.staticList,
            )}`,
            newFilterConfig => {
                if (!newFilterConfig || typeof newFilterConfig !== 'object') {
                    return
                }

                if (Object.keys(newFilterConfig).length === 0) {
                    handleClearFilterRequestByKey('staticListIds', [])
                    return
                }

                filterRequest.value = {
                    ...filterRequest.value,
                    ...newFilterConfig,
                }
            },
        )

        filterDataTableEvent.on(
            `${getFilterEventKey(
                tableTypes.patient,
                filterEventTypes.sicknessLevel,
            )}`,
            newFilterConfig => {
                if (!newFilterConfig || typeof newFilterConfig !== 'object') {
                    return
                }

                if (Object.keys(newFilterConfig).length === 0) {
                    handleClearFilterRequestByKey('sicknessLevels', [])
                    return
                }

                filterRequest.value = {
                    ...filterRequest.value,
                    ...newFilterConfig,
                }
            },
        )

        return () => {
            onPatientTableChangeRemoveListener()
            onPatientPathwayChangeRemoveListener()
            onPatientStaticListChangeRemoveListener()

            removeFilterChangeListener(
                tableTypes.patient,
                filterEventTypes.advanced,
            )

            removeFilterChangeListener(
                tableTypes.patient,
                filterEventTypes.pathway,
            )

            removeFilterChangeListener(tableTypes.patient, filterEventTypes.hcp)

            removeFilterChangeListener(
                tableTypes.patient,
                filterEventTypes.staticList,
            )

            removeFilterChangeListener(
                tableTypes.patient,
                filterEventTypes.sicknessLevel,
            )
        }
    }, [queryData?.content])
    //#endregion

    //#region Export Data

    const handleRetrieveExportData = async (
        {
            exportType = exportTypes.excel, // csv, excel, pdf
            exportRecordType = exportRecordTypes.allRecords, // all, currentInView
            exportColumns = exportColumnTypes.allColumns, // all, currentInView
        },
        columns,
    ) => {
        handleOnExportDatatable(
            {
                exportType: exportType,
                exportRecordType: exportRecordType,
                exportColumns: exportColumns,
            },
            queryData,
            pageable.value,
            filterRequest.value,
            columns,
            tableTypes.patient,
        )
    }
    //#endregion

    return (
        <PageContentWrapper>
            <DatatableContext.Provider
                value={{
                    dataKey: 'id',
                    tableType: tableTypes.patient,
                    queryData: queryData,
                    isFetched: isFetched,
                    columnList: columnList,
                    tableToolbar: [
                        tableToolbarType.tableControlToolbar,
                        tableToolbarType.filterToolbar,
                        tableToolbarType.assignmentToolbar,
                    ],

                    pageable: pageable.value,
                    onChangeSortColumn: handleChangeSortColumn,
                    onChangePageNumber: handleChangePageNumber,
                    onChangePageRows: handleChangePageRows,
                    onPageRowsHovered: handlePrefetchDataByRowsOption,

                    onDetailRowHovered: handlePrefetchDataByDetailRow,
                    onDetailRowClick: handleOnDetailRowClick,

                    handleCheckPermissionByKey: handleCheckPermissionByKey,

                    initialAdvancedFilterRequest: initialAdvancedFilterRequest,
                    handleClearFilterRequest: handleClearFilterRequest,
                    handleClearFilterRequestByKey:
                        handleClearFilterRequestByKey,
                    handleClearAdvancedFilterRequest:
                        handleClearAdvancedFilterRequest,

                    handleRetrieveExportData: handleRetrieveExportData,

                    handleChangeGlobalSearch: handleChangeGlobalSearch,
                    handleAddNew: handleAddNew,
                }}>
                <StyledDataTableWrapper />

                <StyledPatientForm ref={formRef} />
            </DatatableContext.Provider>
        </PageContentWrapper>
    )
}

export default StyledPatientDatatable
